/**
 * ProductMatchAutocomplete — debounced lookup against /api/products/match.
 *
 * Wraps a text input so that as the user types a product_description, a
 * dropdown of catalog suggestions appears. Picking one updates the parent
 * with a normalized {product_description, product_id, manufacturer_sku,
 * match_confidence, unit_of_measure} payload.
 *
 * Free text is always allowed — selecting a product is opt-in.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductMatchAutocomplete = ({
  value,
  onChange,           // (description: str) => void
  onProductSelect,    // (selection: { product_description, product_id, manufacturer_sku, match_confidence, unit_of_measure }) => void
  currentMatchConfidence,    // 0..1 or null — drives the green/yellow indicator
  currentProductId,          // string|null
  currentMatchedName,        // optional — for tooltip
  placeholder = 'Product description',
  inputClassName = '',
  testIdPrefix = 'product-match',
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef(null);
  const lastQueryRef = useRef('');

  const fetchMatches = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/products/match`, { description: q }, { withCredentials: true });
      // Ignore late responses for queries no longer relevant
      if (lastQueryRef.current === q) {
        setSuggestions(res.data?.matches || []);
      }
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    lastQueryRef.current = value;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchMatches(value), 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value, fetchMatches]);

  const handleSelect = (m) => {
    onChange(m.canonical_name);
    onProductSelect({
      product_description: m.canonical_name,
      product_id: m.product_id,
      manufacturer_sku: m.manufacturer_sku || null,
      match_confidence: m.confidence,
      unit_of_measure: m.unit_of_measure,
    });
    setOpen(false);
    setActiveIdx(-1);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIdx(-1);
    }
  };

  // Confidence indicator (matched/suggested) appears INSIDE the input on the right
  const confidence = currentMatchConfidence;
  const hasIndicator = currentProductId && confidence !== null && confidence !== undefined;
  const isConfirmed = hasIndicator && confidence >= 0.8;
  const isSuggested = hasIndicator && confidence >= 0.5 && confidence < 0.8;

  return (
    <div className="relative w-full">
      <input
        type="text"
        value={value || ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        onKeyDown={handleKeyDown}
        data-testid={`${testIdPrefix}-input`}
        className={`${inputClassName} ${hasIndicator ? 'pr-8' : ''}`}
      />

      {hasIndicator && (
        <div
          className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-auto"
          title={
            isConfirmed
              ? `Matched to catalog: ${currentMatchedName || 'product'} (${Math.round(confidence * 100)}%)`
              : isSuggested
                ? `Suggested match: ${currentMatchedName || 'product'} (${Math.round(confidence * 100)}%) — click to confirm or dismiss`
                : ''
          }
          data-testid={`${testIdPrefix}-indicator-${isConfirmed ? 'confirmed' : 'suggested'}`}
        >
          {isConfirmed ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          ) : (
            <AlertCircle className="w-4 h-4 text-amber-500" />
          )}
        </div>
      )}

      {open && (suggestions.length > 0 || loading) && (
        <div
          className="absolute z-50 mt-1 w-full bg-white border-2 border-black shadow-[3px_3px_0_0_rgba(0,0,0,1)] max-h-72 overflow-y-auto"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          {loading && suggestions.length === 0 ? (
            <div className="p-3 text-xs font-mono text-zinc-400">Searching catalog...</div>
          ) : (
            suggestions.map((m, idx) => {
              const pct = Math.round(m.confidence * 100);
              const badgeColor =
                m.confidence >= 0.8 ? 'bg-emerald-100 text-emerald-700' :
                m.confidence >= 0.5 ? 'bg-amber-100 text-amber-700' :
                'bg-zinc-100 text-zinc-600';
              return (
                <button
                  key={m.product_id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()} // avoid blur before click
                  onClick={() => handleSelect(m)}
                  className={`w-full text-left px-3 py-2 border-b border-zinc-200 last:border-b-0 transition-colors ${activeIdx === idx ? 'bg-primary/20' : 'hover:bg-zinc-50'}`}
                  data-testid={`${testIdPrefix}-option-${idx}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-zinc-900 truncate">{m.canonical_name}</p>
                      <p className="text-[10px] font-mono text-zinc-500 mt-0.5 truncate">
                        {m.category}{m.subcategory ? ` · ${m.subcategory}` : ''}{m.manufacturer ? ` · ${m.manufacturer}` : ''}
                      </p>
                    </div>
                    <span className={`flex-shrink-0 text-[10px] font-heading font-bold uppercase tracking-wider px-1.5 py-0.5 ${badgeColor}`}>
                      {pct}%
                    </span>
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default ProductMatchAutocomplete;
